import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Timer, Plus, Save, Trash2, Loader2 } from "lucide-react";
import { SmallClaimLimitCard } from "@/components/SmallClaimLimitCard";

const ACTIONS = ["pm-endorse", "verify", "approve", "disburse", "payment"];
const ACTION_LABEL = { "pm-endorse": "Endorse", verify: "Verify", approve: "Approve", disburse: "Disburse", payment: "Payment" };

export default function SlaSettings() {
  const { user } = useAuth();
  const [defaults, setDefaults] = useState({});
  const [rows, setRows] = useState([]);        // [{department, sla_map}]
  const [dirty, setDirty] = useState({});      // dept -> partial map
  const [newDept, setNewDept] = useState("");
  const [saving, setSaving] = useState(null);  // dept being saved
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/sla");
      setDefaults(r.data.defaults || {});
      setRows(r.data.departments || []);
      setDirty({});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "Admin";
  const getCell = (dept, action) => {
    if (dirty[dept]?.[action] !== undefined) return dirty[dept][action];
    const row = rows.find(r => r.department === dept);
    return row?.sla_map?.[action] ?? defaults[action] ?? 5;
  };

  const setCell = (dept, action, value) => {
    setDirty(prev => ({ ...prev, [dept]: { ...(prev[dept] || {}), [action]: value } }));
  };

  const save = async (dept) => {
    setSaving(dept);
    try {
      const existing = rows.find(r => r.department === dept)?.sla_map || {};
      const merged = { ...defaults, ...existing, ...(dirty[dept] || {}) };
      // Only send positive integers
      const sla_map = {};
      for (const a of ACTIONS) {
        const n = Number(merged[a]);
        if (Number.isFinite(n) && n >= 0) sla_map[a] = Math.floor(n);
      }
      await api.put("/settings/sla", { department: dept, sla_map });
      toast.success(`Saved SLA for ${dept}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  const remove = async (dept) => {
    if (!window.confirm(`Remove SLA overrides for ${dept}? Approvers in that department will fall back to defaults.`)) return;
    try {
      await api.delete(`/settings/sla/${encodeURIComponent(dept)}`);
      toast.success(`Removed ${dept}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const addDept = () => {
    const d = newDept.trim();
    if (!d) return;
    if (rows.some(r => r.department === d)) { toast.error("Department already exists"); return; }
    setRows(prev => [...prev, { department: d, sla_map: {} }]);
    setNewDept("");
    setDirty(prev => ({ ...prev, [d]: { ...defaults } }));
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Settings · SLA</div>
        <h1 className="font-display text-3xl font-extrabold mt-1 flex items-center gap-2"><Timer className="w-6 h-6" /> Approval SLA</h1>
        <p className="text-sm text-slate-500 mt-1">Days after which an item goes red on the Approvals Inbox. Set per department; approvers in departments without an override fall back to defaults.</p>
      </div>

      {!isAdmin && (
        <Card className="border-amber-200 bg-amber-50 mb-4"><CardContent className="p-4 text-sm text-amber-800">Only Admins can edit SLA settings. Values are read-only for your role.</CardContent></Card>
      )}

      <SmallClaimLimitCard isAdmin={isAdmin} />

      <Card className="mb-4"><CardContent className="p-5">
        <div className="text-xs uppercase text-slate-500 mb-2">Platform defaults</div>
        <div className="flex items-center gap-2 flex-wrap">
          {ACTIONS.map(a => (
            <Badge key={a} variant="outline" className="text-xs font-mono">
              {ACTION_LABEL[a]}: <b className="ml-1">{defaults[a] ?? "-"}d</b>
            </Badge>
          ))}
        </div>
      </CardContent></Card>

      {loading ? (<Card><CardContent className="p-10 text-center text-sm text-slate-500">Loading…</CardContent></Card>) : (
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr className="border-b">
                <th className="text-left px-3 py-2 min-w-[160px]">Department</th>
                {ACTIONS.map(a => <th key={a} className="text-center px-3 py-2">{ACTION_LABEL[a]}<span className="block text-[10px] font-mono text-slate-400">days</span></th>)}
                <th className="text-right px-3 py-2 min-w-[160px]"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center px-3 py-12 text-slate-500">
                  <div className="text-sm">No department overrides yet — every user uses the platform defaults above.</div>
                  <div className="text-xs mt-1">Add one below to widen or tighten SLA for a specific team.</div>
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.department} className="border-b border-slate-100" data-testid={`sla-row-${r.department}`}>
                  <td className="px-3 py-3 font-semibold">{r.department}</td>
                  {ACTIONS.map(a => (
                    <td key={a} className="px-2 py-2 text-center">
                      <Input type="number" min={0} value={getCell(r.department, a)}
                        onChange={e => setCell(r.department, a, e.target.value)}
                        disabled={!isAdmin}
                        className="h-9 w-16 text-center font-mono"
                        data-testid={`sla-input-${r.department}-${a}`}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" onClick={() => save(r.department)} disabled={!isAdmin || saving === r.department} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid={`sla-save-${r.department}`}>
                        {saving === r.department ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
                      </Button>
                      {r.department !== "default" && (
                        <Button size="sm" variant="outline" onClick={() => remove(r.department)} disabled={!isAdmin} className="h-8 border-red-200 text-red-700 hover:bg-red-50" data-testid={`sla-delete-${r.department}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      )}

      {isAdmin && (
        <Card className="mt-4">
          <CardContent className="p-4 flex items-end gap-3">
            <div className="flex-1">
              <Label>Add department</Label>
              <Input placeholder="e.g. Accounts, Field Operations…" value={newDept} onChange={e => setNewDept(e.target.value)} data-testid="sla-new-dept-input" />
            </div>
            <Button onClick={addDept} className="bg-[#0F172A]" data-testid="sla-add-dept-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
